import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import {
  Flex,
  Badge,
  Pane,
  Spacer,
  spaces,
  TabList,
  TabPanel,
  TabPanels,
  TabProps,
  TabsContainer,
  Text,
  useBreakpoint,
  useTabContext,
} from '@electricjs/arc';

import AssetError from '@/components/Assets/AssetDetails/AssetError';
import DeviceSecurityPanel from '@/components/Assets/AssetDetails/DeviceSecurityPanel/DeviceSecurityPanel';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetAssetQuery } from '@/redux/slices/assetApiSlice';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import { CenteredSpinner } from '@common';
import AssetImagesGrid from '@/pages/Assets/AssetImagesGrid';
import AssetDetailsPanel from '@/pages/Assets/AssetDetailsContent/AssetDetailsPanel';
import { isDeviceAsset, isDesktopOrLaptopAsset } from '@/types/assets';
import { useGetEmployeeQuery } from '@/redux/slices/employeeApiSlice';
import { AssetDetailsTabs } from '@/pages/Assets/types';
import { AssetHistoryTab } from '@/pages/Assets/AssetHistoryTab';
import { StyledTab } from '@/components/common/StyledTab';
import { DeviceHealthUnknownPanel } from '@/components/Assets/AssetDetails/DeviceSecurityPanel/DeviceHealthUnknownPanel';

const SecurityTab = ({
  id,
  hasIssues,
  issueCount,
  hasMdmEnrolledAt,
  ...otherProps
}: Omit<TabProps, 'children'> & {
  hasIssues?: boolean;
  issueCount?: number;
  hasMdmEnrolledAt?: boolean;
}) => {
  const { t } = useTranslation('assets');
  const { selectedId } = useTabContext();

  const { space_xs_rem } = spaces;

  const showBadge = !!issueCount && issueCount > 0;

  return (
    <StyledTab id={AssetDetailsTabs.Health} {...otherProps}>
      <Flex alignItems="center">
        <Text variant={id === selectedId ? 'label-large' : 'body'}>
          {t('assetDetailsContent.health')}
        </Text>
        {hasIssues && hasMdmEnrolledAt && (
          <>
            <Spacer x={space_xs_rem} />
            {showBadge && <Badge count={issueCount} />}
          </>
        )}
      </Flex>
    </StyledTab>
  );
};

const AssetDetailsContent = () => {
  const { t } = useTranslation('assets');
  const organizationId = useGetOrganizationId();
  const { lg } = useBreakpoint();

  const { assetId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab') ?? AssetDetailsTabs.Details;

  const {
    isLoading: isLoadingAsset,
    isError: isAssetError,
    data: assetData,
  } = useGetAssetQuery(
    {
      organizationId,
      assetId,
    },
    { skip: !assetId }
  );

  const { isFetching: isFetchingEmployee, data: employee } =
    useGetEmployeeQuery(
      {
        employeeId: assetData?.assignedTo || '',
        organizationId: organizationId,
      },
      {
        skip: !assetData?.assignedTo,
      }
    );

  const deviceData = assetData?.device?.reportingData;
  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );

  const hasDeviceInfo = assetData && isDeviceAsset(assetData);
  const showHealthTab =
    hasMdmEntitlement && assetData && isDesktopOrLaptopAsset(assetData);
  const showHealthPanel = hasDeviceInfo && assetData.device.mdmEnrolledAt;
  const hasIssues =
    deviceData?.issues && !!Object.keys(deviceData.issues).length;
  // ignore battery health issue
  const issueCount = Object.keys(deviceData?.issues || {}).filter(
    issue => issue !== 'batteryHealth'
  ).length;

  const tabs = useMemo(() => {
    const baseTabs: React.ReactElement[] = [
      <StyledTab key="details" id={AssetDetailsTabs.Details} width="auto">
        {t('tabs.details')}
      </StyledTab>,
    ];

    if (showHealthTab) {
      baseTabs.push(
        <SecurityTab
          key="health"
          hasIssues={hasIssues}
          issueCount={issueCount}
          hasMdmEnrolledAt={!!assetData?.device?.mdmEnrolledAt}
          id={AssetDetailsTabs.Health}
          width="auto"
        />
      );
    }

    baseTabs.push(
      <StyledTab key="history" id={AssetDetailsTabs.History} width="auto">
        {t('tabs.history')}
      </StyledTab>,
      <StyledTab key="images" id={AssetDetailsTabs.Images} width="auto">
        {t('tabs.images')}
      </StyledTab>
    );

    return baseTabs;
  }, [showHealthTab, hasIssues, issueCount, assetData, t]);

  if (isLoadingAsset) {
    return <CenteredSpinner />;
  }

  return !assetData || isAssetError ? (
    <AssetError />
  ) : (
    <Flex
      gap="2rem"
      flexDirection={lg ? 'row' : 'column'}
      justifyContent="space-evenly">
      <Flex vertical width="100%">
        <TabsContainer selectedId={selectedTab}>
          <Spacer y={4} />
          <TabList>{tabs}</TabList>
          <TabPanels width="100%">
            <AssetDetailsPanel asset={assetData} />
            {showHealthTab ? (
              showHealthPanel ? (
                <DeviceSecurityPanel
                  device={assetData.device}
                  employee={employee}
                  isLoading={isFetchingEmployee}
                />
              ) : (
                <DeviceHealthUnknownPanel asset={assetData} />
              )
            ) : (
              <></>
            )}
            <AssetHistoryTab assetId={assetId} />
            <TabPanel tabId={AssetDetailsTabs.Images}>
              <Spacer y="2rem" />
              <Pane flexDirection="column" width="100%">
                <AssetImagesGrid assetId={assetId} />
              </Pane>
            </TabPanel>
          </TabPanels>
        </TabsContainer>
      </Flex>
    </Flex>
  );
};

export default AssetDetailsContent;
